import { get } from 'lodash';
import { onboardingTasks } from 'client/GrowthEngineering/utilities/GrowthConstants';
import ApplicationApi from 'client/api/v2/ApplicationApi';
import Constants, { OnboardingChecklistConsts } from 'util/Constants';
import DirectoryApi from 'client/api/v2/DirectoryApi';
import EventManager from 'client/GrowthEngineering/EventManager';
import ModalEventBus from 'components/Modal/ModalEventBus';
import RadiusServerApi from 'client/api/v2/RadiusServerApi';
import SystemApi from 'client/api/v2/SystemApi';
import SystemUserApi from 'client/api/v2/SystemUserApi';
import UserGroupApi from 'client/api/v2/UserGroupApi';
import VideoModal from 'client/components/Modal/VideoModal.vue';
import axios from 'axios';

const { linux, mac, windows } = Constants;
const {
  wistiaLinuxSystemSetupVideo,
  wistiaMacSystemSetupVideo,
  wistiaWindowsSystemSetupVideo,
} = OnboardingChecklistConsts;
const {
  triggerOnboardingChecklistModalAddSystemsInBulkLinkClicked,
  triggerSystemSetupModalDismissed,
} = EventManager;

const redirect = url => window.location.assign(url);

const tasks = {
  createAdminAccount: {
    title: onboardingTasks.createAdminAccount.title,
    handleClick: () => {},
    checkIfComplete: () => Promise.resolve(true),
  },
  discoverResources: {
    title: onboardingTasks.discoverResources.title,
    handleClick: () => {},
    checkIfComplete: () => {
      const fetchTasks = [
        ApplicationApi.fetch(0, 1),
        DirectoryApi.fetch(0, 1),
        RadiusServerApi.fetch(0, 1),
        SystemApi.fetch(0, 1),
      ];

      return Promise.all(fetchTasks)
        .then(([appData, directoryData, radiusData, systemData]) => (
          appData.totalCount > 0 || directoryData.totalCount > 1
            || radiusData.totalCount > 0 || systemData.totalCount > 0
        ));
    },
  },
  systemCreated: {
    title: onboardingTasks.systemCreated.title,
    handleClick: () => {
      EventManager.triggerSystemSetupModalViewed();
      const kbLinkObject = {
        action: () => triggerOnboardingChecklistModalAddSystemsInBulkLinkClicked(),
        url: 'https://support.jumpcloud.com/support/s/article/agent-deployment-via-command-line1',
        text: 'Add systems in bulk',
      };
      ModalEventBus.open({
        component: VideoModal,
        isLargeModal: true,
        componentProps: {
          header: 'Set up a System',
          modalClosedAction: () => triggerSystemSetupModalDismissed(),
          videos: [
            {
              tabName: windows,
              iframeUrl: wistiaWindowsSystemSetupVideo,
              videoUrl: wistiaWindowsSystemSetupVideo,
              kbLinkObject,
            },
            {
              tabName: mac,
              iframeUrl: wistiaMacSystemSetupVideo,
              videoUrl: wistiaMacSystemSetupVideo,
              kbLinkObject,
            },
            {
              tabName: linux,
              iframeUrl: wistiaLinuxSystemSetupVideo,
              videoUrl: wistiaLinuxSystemSetupVideo,
              kbLinkObject,
            },
          ],
        },
      });
    },
    checkIfComplete: () => (
      SystemApi.fetch(0, 1)
        .then(data => get(data, 'results.length', 0) > 0)
    ),
  },
  userBoundToUserGroup: {
    title: onboardingTasks.userBoundToUserGroup.title,
    handleClick: (go = redirect) => {
      // Open the group details aside for the admin's first user group, if any
      UserGroupApi.fetch(0, 1).then((data) => {
        if (data.results && data.results.length) {
          const firstGroupId = data.results[0].id;
          go(`/#/groups/user/${firstGroupId}/details`);
        } else {
          go('/#/groups');
        }
      });
    },
    checkIfComplete: () => (
      UserGroupApi.fetch(0, 10)
        .then((data) => {
          const numGroups = get(data, 'results.length', 0);
          if (!numGroups) return false;

          // If they have more than 10 groups, consider this task done to avoid having to do
          // a computationally expensive task
          if (numGroups > 10) return true;

          const hasUserBoundTasks = data.results.map(g => (
            axios.get(`/api/v2/usergroups/${g.id}/membership`)
              .then(resp => resp.data.length > 0)
          ));
          return Promise.all(hasUserBoundTasks)
            .then(results => results.some(r => r));
        })
    ),
  },
  userBoundToDevice: {
    title: onboardingTasks.userBoundToDevice.title,
    handleClick: () => {},
    checkIfComplete: () => (
      SystemUserApi.fetch(0, 10)
        .then((users) => {
          const numUsers = get(users, 'results.length', 0);
          if (!numUsers) return false;

          // If they have more than 10 users, consider this task done to avoid having to do
          // a computationally expensive task
          if (numUsers > 10) return true;

          const hasUserBoundTasks = users.results.map(user => (
            axios.get(`/api/v2/users/${user.id}/systems`)
              .then(resp => resp.data.length > 0)
          ));
          return Promise.all(hasUserBoundTasks)
            .then(results => results.some(r => r));
        })
    ),
  },
  userCreated: {
    title: onboardingTasks.userCreated.title,
    handleClick: (go = redirect) => go('/#/users/add'),
    checkIfComplete: () => (
      SystemUserApi.fetch(0, 1)
        .then(data => get(data, 'results.length', 0) > 0)
    ),
  },
  userGroupCreated: {
    title: onboardingTasks.userGroupCreated.title,
    handleClick: (go = redirect) => go('/#/groups/user/new'),
    checkIfComplete: () => (
      UserGroupApi.fetch(0, 1)
        .then(data => get(data, 'results.length', 0) > 0)
    ),
  },
};

const defaultOnboardingTaskList = [
  tasks.userCreated,
  tasks.userGroupCreated,
  tasks.userBoundToUserGroup,
  tasks.discoverResources,
];

const gettingStartedOnboardingTaskList = [
  tasks.createAdminAccount,
  tasks.systemCreated,
  tasks.userCreated,
  tasks.userBoundToDevice,
];

/**
 * This method returns the onboarding tasks that should be given to the admin.
 * If no experiment is currently running it returns the default list
 */
const getOnboardingTaskList = (gettingStartedList = false) => (
  gettingStartedList ? gettingStartedOnboardingTaskList : defaultOnboardingTaskList
);

/**
 * A list of tasks (tracked by checklists) that a user may perform.
 * @param {String} title - The title of the task shown to admins. Must be unique.
 * @param {String} [event] - The name of the event to trigger on task completion (optional)
 * @param {Function} handleClick - Executed when admin clicks on title of task.
 */
export default {
  getOnboardingTaskList,
  ...tasks,
};
